import React from 'react';
import { type UserVideo } from '../../api/videoApi';

// Helper function to create pinned video object
const createPinnedVideo = (video: UserVideo, userEmail: string): UserVideo => ({
  ...video,
  is_pinned: true,
  pinned_at: new Date().toISOString(),
  pinned_by: userEmail
});

// Helper function to add video to pinned list
const addToPinnedVideos = (
  currentPinned: UserVideo[],
  videoToPin: UserVideo,
  videoIdStr: string,
  userEmail: string
): UserVideo[] => {
  if (!currentPinned.some(pv => String(pv.id) === videoIdStr)) {
    const pinnedVideo = createPinnedVideo(videoToPin, userEmail);
    return [pinnedVideo, ...currentPinned];
  }
  return currentPinned;
};

// Helper function to update video pin status
const updateVideosPinStatus = (
  currentVideos: UserVideo[],
  videoIdStr: string,
  userEmail: string
): UserVideo[] => {
  return currentVideos.map(v => {
    if (String(v.id) === videoIdStr) {
      return createPinnedVideo(v, userEmail);
    }
    return v;
  });
};

// Helper function to update videos unpin status
const updateVideosUnpinStatus = (
  currentVideos: UserVideo[],
  videoIdStr: string
): UserVideo[] => {
  return currentVideos.map(v => {
    if (String(v.id) === videoIdStr) {
      return {
        ...v,
        is_pinned: false,
        pinned_at: undefined,
        pinned_by: undefined
      };
    }
    return v;
  });
};

// Optimistic update functions for pin/unpin operations
export const createOptimisticPinVideo = (
  setVideos: React.Dispatch<React.SetStateAction<UserVideo[]>>,
  setPinnedVideos: React.Dispatch<React.SetStateAction<UserVideo[]>>,
  setIsAllowedToPin?: React.Dispatch<React.SetStateAction<boolean>>
) => {
  return (videoId: string, userEmail: string) => {
    const videoIdStr = String(videoId);
    
    setVideos(currentVideos => {
      const videoToPin = currentVideos.find(video => String(video.id) === videoIdStr);
      
      if (videoToPin) {
        // Add to pinned videos immediately
        setPinnedVideos(currentPinned => 
          addToPinnedVideos(currentPinned, videoToPin, videoIdStr, userEmail)
        );
        
        if (setIsAllowedToPin) {  
          setIsAllowedToPin(false);
        }
        
        return updateVideosPinStatus(currentVideos, videoIdStr, userEmail);
      }
      
      return currentVideos;
    });
  };
};

export const createOptimisticUnpinVideo = (
  setVideos: React.Dispatch<React.SetStateAction<UserVideo[]>>,
  setPinnedVideos: React.Dispatch<React.SetStateAction<UserVideo[]>>,
  setIsAllowedToPin?: React.Dispatch<React.SetStateAction<boolean>>
) => {
  return (videoId: string) => {
    const videoIdStr = String(videoId);
    
    setPinnedVideos(currentPinned => {
      return currentPinned.filter(v => String(v.id) !== videoIdStr);
    });
    
    if (setIsAllowedToPin) {
      setIsAllowedToPin(true);
    }
    
    setVideos(currentVideos => updateVideosUnpinStatus(currentVideos, videoIdStr));
  };
};

// Shared pin/unpin handlers that work for both admin and user
export const createPinHandler = (
  optimisticPinVideo: (videoId: string, userEmail: string) => void,
  _pinVideoWebSocket: (videoId: string, userEmail?: string) => void,
  userEmail: string,
  _context: 'admin' | 'user' = 'user'
) => {
  return (video: UserVideo) => {
    // React Query mutations already handle API calls + optimistic updates
    // Just call the mutation function which handles everything
    optimisticPinVideo(video.id, userEmail || video.user_email || '');
    
    // Note: WebSocket notification is now handled by the React Query mutation's onSuccess
    // so we don't need to call pinVideoWebSocket here anymore
  };
};

export const createUnpinHandler = (
  optimisticUnpinVideo: (videoId: string) => void,
  _unpinVideoWebSocket: (videoId: string, userEmail?: string) => void,
  _context: 'admin' | 'user' = 'user'
) => {
  return (video: UserVideo) => {
    // React Query mutations already handle API calls + optimistic updates
    // Just call the mutation function which handles everything
    optimisticUnpinVideo(video.id);
    
    // Note: WebSocket notification is now handled by the React Query mutation's onSuccess
    // so we don't need to call unpinVideoWebSocket here anymore
  };
};

// Hook factory that creates optimistic update functions
export const useOptimisticPinUnpin = (
  setVideos: React.Dispatch<React.SetStateAction<UserVideo[]>>,
  setPinnedVideos: React.Dispatch<React.SetStateAction<UserVideo[]>>,
  setIsAllowedToPin?: React.Dispatch<React.SetStateAction<boolean>>
) => {
  const optimisticPinVideo = React.useCallback(
    createOptimisticPinVideo(setVideos, setPinnedVideos,setIsAllowedToPin),
    [setVideos, setPinnedVideos,setIsAllowedToPin]
  );

  const optimisticUnpinVideo = React.useCallback(
    createOptimisticUnpinVideo(setVideos, setPinnedVideos,setIsAllowedToPin),
    [setVideos, setPinnedVideos,setIsAllowedToPin]
  );

  return {
    optimisticPinVideo,
    optimisticUnpinVideo
  };
};


